import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { Api } from '../../../api';
import Loader from '../../../component/common/Loader';
import {
  StatCard,
  Panel,
  PanelEmpty,
  EntityHeader,
  MiniDonutCard,
  ProgressList,
  StandardDropdown,
  STAT_ICONS,
  FEATURE_ICONS,
  CHART_BASE,
  CHART_GRID,
  CHART_COLORS
} from '../../../component/common/designSystem';

/**
 * The PEAP dashboard, on the design system.
 *
 * Replaces components/insights.js, which rendered the same payload through
 * bespoke gradient cards, a custom font, two charting libraries and SEVEN
 * independent period dropdowns — one per card. Every figure on the page now
 * answers to one period selector, the rule the B2B dashboard set: a page where
 * each card can be showing a different month is a page nobody can read across.
 *
 * The payload is unchanged (GET admin/v1/peap-dashboard). Every field the old
 * page read is still shown; nothing was dropped in the move.
 */

const PERIODS = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'This month', value: 'month' },
  { label: 'This year', value: 'year' }
];

/**
 * Bar labels per period. The payload returns the counts positionally, so the
 * labels have to come from here — the same sets the old page used.
 */
const BAR_LABELS = {
  today: [],
  week: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  month: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
  year: ['Q1', 'Q2', 'Q3', 'Q4']
};

const count = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0');

/**
 * "+12% on last month" / "-4% on last week". Null when there is no change to
 * report, so the card shows no note rather than "0% increase".
 */
const changeNote = (value, period) => {
  const pct = Math.round(parseFloat(value));
  if (!Number.isFinite(pct) || pct === 0) return null;
  const against = period === 'today' ? 'yesterday' : `last ${period}`;
  return `${pct > 0 ? '+' : ''}${pct}% on ${against}`;
};

/** `[{ name, value }]` → the `[{ label, count }]` the chart primitives take. */
const asDistribution = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    label: row?.name || row?.label || 'Unknown',
    count: Number(row?.value) || 0
  }));

const PeapInsights = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Api.getPeapDashboard()
      .then((response) => {
        if (!cancelled && response?.data?.meta?.code === 200) {
          setData(response.data.data);
        }
      })
      .catch((error) => console.error('PEAP dashboard failed to load', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const stats = data?.userStats || {};
  const periodLabel = PERIODS.find((option) => option.value === period)?.label || '';

  const sessionSeries = useMemo(() => {
    const rows = data?.sessionCountByDays?.[period] || [];
    const labels = BAR_LABELS[period] || [];
    return {
      labels: rows.map((row, index) => row?.name || row?.label || labels[index] || `${index + 1}`),
      values: rows.map((row) => Number(row?.value) || 0)
    };
  }, [data, period]);

  const barOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: sessionSeries.labels },
    yaxis: { labels: { formatter: (value) => Math.round(value) } },
    tooltip: { y: { formatter: (value) => `${value} sessions` } }
  };

  const topDoctor = data?.topDoctor;

  return (
    <div className="mt-6">
      {loading ? <Loader /> : null}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing {periodLabel.toLowerCase()}
        </p>
        <StandardDropdown
          label="Period"
          options={PERIODS}
          value={period}
          onChange={setPeriod}
          variant="secondary"
          align="right"
        />
      </div>

      {/* Headline figures. The first three are all-time; the last two follow the period. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <StatCard
          icon={STAT_ICONS.users}
          title="Members supported"
          value={count(data?.totalMembersSupported)}
        />
        <StatCard
          icon={STAT_ICONS.session}
          title="Total sessions"
          value={count(data?.totalSessions)}
        />
        <StatCard
          icon={STAT_ICONS.specialist}
          title="Work-related cases"
          value={count(data?.workRelated)}
        />
        <StatCard
          icon={FEATURE_ICONS.peap}
          title="WellPoints used"
          value={count(stats?.wellpoints?.[period])}
          note={changeNote(stats?.wellpointsPercentageChange?.[period], period)}
        />
        <StatCard
          icon={STAT_ICONS.newUsers}
          title="New users"
          value={count(stats?.user?.[period])}
          note={changeNote(stats?.userPercentageChange?.[period], period)}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4">
        {/* The most-booked specialist this month. */}
        {topDoctor?.name ? (
          <EntityHeader
            image={topDoctor.profile}
            name={topDoctor.name}
            subtitle={topDoctor.job_title}
            chips={
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Top booked specialist
              </span>
            }
            action={
              <div>
                <p className="text-[24px] leading-tight font-semibold text-shoorah-btn dark:text-white">
                  {count(topDoctor.totalSessions)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">sessions this month</p>
              </div>
            }
          />
        ) : (
          <Panel title="Top booked specialist">
            <PanelEmpty>No sessions booked yet this month.</PanelEmpty>
          </Panel>
        )}

        <Panel
          title="Sessions"
          subtitle={`Booked ${periodLabel.toLowerCase()}`}
          className="xl:col-span-2"
        >
          {sessionSeries.values.some((value) => value > 0) ? (
            <Chart
              type="bar"
              height={240}
              options={barOptions}
              series={[{ name: 'Sessions', data: sessionSeries.values }]}
            />
          ) : (
            <PanelEmpty>No sessions in this period.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <MiniDonutCard
          title="Most popular sessions"
          subtitle={periodLabel}
          data={asDistribution(data?.popularJobTitles?.[period])}
          unit="sessions"
        />
        <MiniDonutCard
          title="Age"
          subtitle={periodLabel}
          data={asDistribution(stats?.age?.[period])}
          unit="users"
        />
        <MiniDonutCard
          title="Gender"
          subtitle={periodLabel}
          data={asDistribution(stats?.gender?.[period])}
          unit="users"
        />
      </div>

      <Panel title="Sessions by job grade" subtitle="All time" className="mt-4">
        <ProgressList
          items={(data?.jobRoleStats || []).map((row) => ({
            label: row?.jobRole || 'Unknown',
            count: Number(row?.count) || 0
          }))}
          unit="sessions"
        />
      </Panel>
    </div>
  );
};

export default PeapInsights;
